/**
 * Homepage "how it works" — two starting points that converge into one
 * eight-step chain, directly under the hero (2026-10 concrete-use-case pass,
 * two-entry-point follow-up).
 *
 * Starting points: a customer report through support, or a bug engineering
 * already knows about (filed in FixControl or worked in a code session) — so
 * support never reads as a mandatory entry point. The two cards are a plain
 * list; the merge line under them is decorative (aria-hidden) and the
 * sentence below it carries the meaning, so the convergence survives stacking
 * on phones and screen readers alike.
 *
 * The steps are grouped into the three phases that answer a first-time
 * visitor's real questions: what FixControl does on its own (investigate,
 * read-only), what it prepares without touching the repository (propose and
 * test), and where people stay in charge (your team decides). Text is the
 * source of truth — the list reads correctly without any styling: one
 * heading per phase, an ordered list per phase that continues the numbering
 * (`start`), so a screen reader hears steps 1–8 in order.
 *
 * Layout: one column on phones, two on tablets, and on desktop the four
 * investigation steps fill the first row while "propose and test" and "your
 * team decides" share the second — so the chain reads left-to-right,
 * top-to-bottom with no arrows to maintain. The decision phase carries the
 * brand accent: it is the part a visitor must not miss.
 */

import Link from "next/link";
import { ArrowRight, Bug, FlaskConical, MessageSquare, Search, UserCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Reveal } from "@/components/reveal";
import { useT } from "@/i18n";

type StepKey = "s1" | "s2" | "s3" | "s4" | "s5" | "s6" | "s7" | "s8";

const ENTRIES = [
  { key: "support", Icon: MessageSquare },
  { key: "engineering", Icon: Bug },
] as const;

const ENTRY_PATH = ["p1", "p2", "p3"] as const;

const PHASES: ReadonlyArray<{
  key: "investigate" | "propose" | "decide";
  Icon: LucideIcon;
  steps: readonly StepKey[];
  /** Desktop column span of the phase in the 4-column grid. */
  span: string;
  /** Step grid inside the phase. */
  grid: string;
  accent: boolean;
}> = [
  {
    key: "investigate",
    Icon: Search,
    steps: ["s1", "s2", "s3", "s4"],
    span: "lg:col-span-4",
    grid: "sm:grid-cols-2 lg:grid-cols-4",
    accent: false,
  },
  {
    key: "propose",
    Icon: FlaskConical,
    steps: ["s5", "s6"],
    span: "lg:col-span-2",
    grid: "sm:grid-cols-2",
    accent: false,
  },
  {
    key: "decide",
    Icon: UserCheck,
    steps: ["s7", "s8"],
    span: "lg:col-span-2",
    grid: "sm:grid-cols-2",
    accent: true,
  },
];

/** First step number of each phase, so the numbering continues across the
 *  three ordered lists (1–4, 5–6, 7–8). */
const PHASE_STARTS = PHASES.map((_, i) =>
  PHASES.slice(0, i).reduce((n, p) => n + p.steps.length, 1),
);

export function WorkflowSection() {
  const t = useT();
  return (
    <section id="how" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-balance text-3xl font-semibold leading-[1.1] tracking-tight text-fg-bright md:text-5xl">
              {t("landing.flow.title")}
            </h2>
            <p className="mt-5 text-pretty text-[15px] leading-relaxed text-fg-dim md:text-base">
              {t("landing.flow.intro")}
            </p>
          </div>
        </Reveal>

        <ul
          aria-label={t("landing.flow.entriesLabel")}
          className="mx-auto mt-12 grid max-w-5xl gap-4 md:grid-cols-2"
        >
          {ENTRIES.map(({ key, Icon }, i) => (
            <Reveal as="li" key={key} delay={i * 80} className="card flex flex-col p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <Icon size={17} aria-hidden className="shrink-0 text-brand-300" />
                <h3 className="text-[16px] font-semibold text-fg-bright">
                  {t(`landing.flow.entries.${key}.title`)}
                </h3>
              </div>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-fg-dim">
                {t(`landing.flow.entries.${key}.body`)}
              </p>
              <ol className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[12.5px] text-fg">
                {ENTRY_PATH.map((p, j) => (
                  <li key={p} className="flex items-center gap-1.5">
                    {j > 0 && <ArrowRight size={13} aria-hidden className="text-fg-muted" />}
                    <span className="rounded-full border border-line-subtle px-2.5 py-1">
                      {t(`landing.flow.entries.${key}.path.${p}`)}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-auto pt-4 text-[12.5px] text-fg-muted">
                {t(`landing.flow.entries.${key}.note`)}
              </p>
            </Reveal>
          ))}
        </ul>

        {/* Merge line: two stubs from the card centres joining into one on
            tablet/desktop; a single stub when the cards stack on phones. */}
        <div aria-hidden className="relative mx-auto h-10 max-w-5xl">
          <span className="absolute left-1/4 top-0 hidden h-5 w-px bg-brand-500/40 md:block" />
          <span className="absolute right-1/4 top-0 hidden h-5 w-px bg-brand-500/40 md:block" />
          <span className="absolute left-1/4 right-1/4 top-5 hidden h-px bg-brand-500/40 md:block" />
          <span className="absolute left-1/2 top-0 h-10 w-px bg-brand-500/40 md:top-5 md:h-5" />
        </div>
        <Reveal delay={160}>
          <p className="text-center text-[15px] font-medium text-fg-bright">
            {t("landing.flow.converge")}
          </p>
        </Reveal>

        <div
          role="group"
          aria-label={t("landing.flow.listLabel")}
          className="mt-10 grid gap-x-4 gap-y-10 lg:grid-cols-4"
        >
          {PHASES.map(({ key, Icon, steps, span, grid, accent }, phaseIndex) => {
            const start = PHASE_STARTS[phaseIndex];
            return (
              <Reveal key={key} delay={phaseIndex * 80} className={span}>
                <div
                  className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t-2 pt-3 ${
                    accent ? "border-brand-500/60" : "border-line-subtle"
                  }`}
                >
                  <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-fg-bright">
                    <Icon
                      size={15}
                      aria-hidden
                      className={accent ? "text-brand-300" : "text-fg-muted"}
                    />
                    {t(`landing.flow.phases.${key}.title`)}
                  </h3>
                  <span className="text-[12.5px] text-fg-muted">
                    {t(`landing.flow.phases.${key}.note`)}
                  </span>
                </div>
                <ol start={start} className={`mt-4 grid gap-3 ${grid}`}>
                  {steps.map((step, i) => (
                    <li
                      key={step}
                      className={`card flex h-full gap-3 p-4 ${
                        accent ? "border-brand-500/30 bg-brand-500/5" : ""
                      }`}
                    >
                      <span
                        aria-hidden
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/10 text-[12px] font-semibold text-brand-300"
                      >
                        {start + i}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold leading-snug text-fg-bright">
                          {t(`landing.flow.steps.${step}.title`)}
                        </div>
                        <p className="mt-1.5 text-[13px] leading-relaxed text-fg-dim">
                          {t(`landing.flow.steps.${step}.body`)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={240}>
          <div className="mt-14 flex flex-col items-center gap-5 text-center">
            <p className="max-w-3xl text-balance text-xl font-medium leading-snug tracking-tight text-fg-bright md:text-2xl">
              {t("landing.flow.principle")}
            </p>
            <Link
              href="#example"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-300 transition hover:text-brand-200"
            >
              {t("landing.flow.cta")}
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
